export const GUIDE_CONTENT = {
  guide_formats: {
    title: "WebP vs JPG vs PNG: qual usar em cada caso",
    content: `
      <p>Escolher o formato correto de imagem evita retrabalho, melhora o carregamento do site e mantém a qualidade visual adequada para cada contexto.</p>

      <h3>WebP</h3>
      <p>É indicado para uso geral na web porque oferece excelente compressão com boa qualidade. Na prática, costuma gerar arquivos menores do que JPG e PNG em muitos cenários.</p>
      <ul>
        <li>Use em banners, imagens de blog e páginas de produto.</li>
        <li>Ajuda no desempenho e nos Core Web Vitals.</li>
        <li>Evite apenas quando houver necessidade de compatibilidade com sistemas muito antigos.</li>
      </ul>

      <h3>JPG/JPEG</h3>
      <p>É uma boa opção para fotografias e imagens com muitas variações de cor. O foco é reduzir tamanho, com perda controlada de qualidade.</p>
      <ul>
        <li>Use em galerias de fotos e conteúdo para redes sociais.</li>
        <li>Não é recomendado para imagens com transparência.</li>
        <li>Evite em textos muito pequenos e gráficos com bordas precisas.</li>
      </ul>

      <h3>PNG</h3>
      <p>É o formato mais indicado quando você precisa de transparência e nitidez em elementos gráficos.</p>
      <ul>
        <li>Use em logotipos, ícones e imagens com fundo transparente.</li>
        <li>Mantém ótima qualidade em textos e traços.</li>
        <li>Normalmente gera arquivos maiores que WebP e JPG.</li>
      </ul>
    `,
  },
  guide_ecommerce: {
    title: "Como preparar imagens para e-commerce",
    content: `
      <p>No e-commerce, imagem pesada reduz conversão e aumenta abandono. O objetivo é equilibrar boa apresentação do produto e carregamento rápido.</p>

      <h3>1. Padronize dimensões</h3>
      <p>Defina tamanhos consistentes para vitrine, página de produto e miniaturas. Isso melhora o layout e reduz variações visuais.</p>

      <h3>2. Escolha o formato correto</h3>
      <ul>
        <li><strong>WebP:</strong> opção principal para fotos de produto.</li>
        <li><strong>PNG:</strong> para logotipos e artes com transparência.</li>
        <li><strong>JPG:</strong> alternativa quando WebP não for possível.</li>
      </ul>

      <h3>3. Otimize o peso</h3>
      <p>Evite subir arquivos muito maiores do que a área de exibição real. Uma imagem de 3000 px para um card pequeno tende a desperdiçar banda e tempo de carregamento.</p>

      <h3>4. Nomeie os arquivos com clareza</h3>
      <p>Use nomes descritivos, por exemplo: <em>camisa-algodao-azul-frente.webp</em>. Isso facilita a organização interna e pode ajudar no SEO de imagem.</p>
    `,
  },
  guide_favicon: {
    title: "Como criar favicon correto (ICO, PNG e tamanhos ideais)",
    content: `
      <p>O favicon é um detalhe pequeno, mas importante para a identidade da marca em abas, favoritos e atalhos.</p>

      <h3>Formato recomendado</h3>
      <p>Use <strong>ICO</strong> como arquivo principal, pois ele mantém ampla compatibilidade com navegadores.</p>

      <h3>Tamanhos úteis</h3>
      <ul>
        <li>16x16 px: exibição em abas.</li>
        <li>32x32 px: favoritos e variações de interface.</li>
        <li>48x48 px ou maior: alguns contextos de atalhos e sistemas.</li>
      </ul>

      <h3>Boas práticas</h3>
      <ul>
        <li>Evite muitos detalhes; o ícone é exibido em tamanho reduzido.</li>
        <li>Prefira alto contraste para facilitar a leitura visual.</li>
        <li>Mantenha uma versão em PNG para usos complementares no site.</li>
      </ul>
    `,
  },
  guide_compression: {
    title: "Como reduzir o peso da imagem sem perder qualidade visual",
    content: `
      <p>Nem sempre é necessário escolher entre qualidade e desempenho. Com alguns ajustes, é possível obter bons resultados nos dois pontos.</p>

      <h3>1. Reduza as dimensões ao necessário</h3>
      <p>Ajuste largura e altura para o tamanho real de exibição. Isso já costuma gerar redução relevante no arquivo.</p>

      <h3>2. Use o formato certo para cada tipo</h3>
      <ul>
        <li>Fotos: WebP ou JPG com qualidade controlada.</li>
        <li>Gráficos com transparência: PNG ou WebP com alpha.</li>
        <li>Favicons: ICO.</li>
      </ul>

      <h3>3. Evite múltiplas recompressões</h3>
      <p>Converter um arquivo várias vezes pode degradar a imagem. Sempre que possível, trabalhe a partir do original.</p>

      <h3>4. Teste visualmente</h3>
      <p>Depois de converter, compare a imagem em telas diferentes (desktop e mobile) para validar nitidez e fidelidade.</p>
    `,
  },
  guide_cwv: {
    title: "Checklist de imagens para melhorar Core Web Vitals",
    content: `
      <p>Imagens impactam diretamente métricas de desempenho, principalmente LCP e velocidade percebida.</p>

      <h3>Checklist rápido</h3>
      <ul>
        <li>Use WebP para a maioria das imagens de conteúdo.</li>
        <li>Evite imagens maiores do que o espaço real de renderização.</li>
        <li>Priorize compressão equilibrada entre peso e qualidade.</li>
        <li>Defina dimensões para reduzir mudanças de layout.</li>
        <li>Mantenha consistência visual em miniaturas e banners.</li>
      </ul>

      <h3>Resultado esperado</h3>
      <p>Com imagens otimizadas, o site tende a carregar mais rápido, melhorar a experiência do usuário e aumentar a eficiência em SEO técnico.</p>
    `,
  },
} as const;